import React, { useState, useEffect } from 'react';
import { siteService } from '../services/siteService';
import { customerService } from '../services/customerService';
import { vatRateService } from '../services/vatRateService';
import CustomerModal from './CustomerModal';
import SiteModal from './SiteModal';

const JobModal = ({ job, onSave, onClose, defaultCustomerId }) => {
  const [formData, setFormData] = useState({
    site_id: '',
    title: '',
    customer_po: '',
    description: '',
    value: '',
    vat_rate_id: '',
    status: 'Quoted',
  });
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(defaultCustomerId ? String(defaultCustomerId) : '');
  const [vatRates, setVatRates] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showNewSiteModal, setShowNewSiteModal] = useState(false);

  const isEditing = !!job;

  useEffect(() => {
    siteService.getSites().then(res => setSites((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    customerService.getCustomers().then(res => setCustomers((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    vatRateService.getVatRates().then(res => setVatRates(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (job && sites.length) {
      const matchingSite = sites.find(s => String(s.id) === String(job.site_id));
      setSelectedCustomer(matchingSite ? String(matchingSite.customer_id) : '');
    }
  }, [job, sites]);

  useEffect(() => {
    if (job) {
      setFormData({
        site_id: job.site_id || '',
        title: job.title || '',
        customer_po: job.customer_po || '',
        description: job.description || '',
        value: job.value !== null && job.value !== undefined ? job.value : '',
        vat_rate_id: job.vat_rate_id || '',
        status: job.status || 'Quoted',
      });
    }
  }, [job]);

  const filteredSites = selectedCustomer
    ? sites.filter(s => String(s.customer_id) === selectedCustomer)
    : sites;

  const handleCustomerChange = (e) => {
    setSelectedCustomer(e.target.value);
    setFormData(prev => ({ ...prev, site_id: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await onSave(formData);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'An error occurred. Please try again.' });
      }
    }

    setLoading(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-600 ${
      errors[field]
        ? 'border-red-500 focus:border-red-500'
        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
    }`;

  const netValue = parseFloat(formData.value);
  const selectedVatRate = vatRates.find(r => String(r.id) === String(formData.vat_rate_id));
  const vatRate = selectedVatRate ? parseFloat(selectedVatRate.rate) : null;
  const vatAmount = vatRate !== null && !isNaN(netValue) && netValue > 0
    ? netValue * (vatRate / 100)
    : null;
  const totalValue = vatAmount !== null ? netValue + vatAmount : null;

  const formatCurrency = (val) =>
    `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Job' : 'Add New Job'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl font-semibold leading-none transition-colors">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {errors.general}
            </div>
          )}

          {/* Customer filter */}
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Customer <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              <select value={selectedCustomer} onChange={handleCustomerChange} className={inputClass('customer_id') + ' flex-1'}>
                <option value="">Select a customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCustomerModal(true)}
                className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap"
              >+ New</button>
            </div>
          </div>

          {/* Site select */}
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Site <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              <select name="site_id" value={formData.site_id} onChange={handleChange} disabled={!selectedCustomer} className={inputClass('site_id') + ' flex-1'}>
                <option value="">Select a site...</option>
                {filteredSites.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewSiteModal(true)}
                disabled={!selectedCustomer}
                className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >+ New</button>
            </div>
            {errors.site_id && <p className="text-red-500 dark:text-red-400 text-sm">{errors.site_id[0]}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Title <span className="text-red-500">*</span></label>
            <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="Job title" disabled={!formData.site_id} className={inputClass('title')} />
            {errors.title && <p className="text-red-500 dark:text-red-400 text-sm">{errors.title[0]}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Customer PO</label>
            <input type="text" name="customer_po" value={formData.customer_po} onChange={handleChange} placeholder="Customer purchase order number" disabled={!formData.site_id} className={inputClass('customer_po')} />
            {errors.customer_po && <p className="text-red-500 dark:text-red-400 text-sm">{errors.customer_po[0]}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Job description" rows={4} disabled={!formData.site_id} className={inputClass('description') + ' resize-none'} />
            {errors.description && <p className="text-red-500 dark:text-red-400 text-sm">{errors.description[0]}</p>}
          </div>

          {/* Value + VAT Rate side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">Value (£)</label>
              <input type="number" name="value" value={formData.value} onChange={handleChange} placeholder="0.00" step="0.01" min="0" disabled={!formData.site_id} className={inputClass('value')} />
              {errors.value && <p className="text-red-500 dark:text-red-400 text-sm">{errors.value[0]}</p>}
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">VAT Rate</label>
              <select name="vat_rate_id" value={formData.vat_rate_id} onChange={handleChange} disabled={!formData.site_id} className={inputClass('vat_rate_id')}>
                <option value="">No VAT</option>
                {vatRates.map(r => (
                  <option key={r.id} value={r.id}>{parseFloat(r.rate)}%</option>
                ))}
              </select>
              {errors.vat_rate_id && <p className="text-red-500 dark:text-red-400 text-sm">{errors.vat_rate_id[0]}</p>}
            </div>
          </div>

          {/* Total inc. VAT */}
          {totalValue !== null && (
            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <span>Net: <strong className="text-gray-700 dark:text-gray-200">{formatCurrency(netValue)}</strong></span>
                <span className="mx-2">+</span>
                <span>VAT ({vatRate}%): <strong className="text-gray-700 dark:text-gray-200">{formatCurrency(vatAmount)}</strong></span>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide font-medium">Total inc. VAT</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(totalValue)}</p>
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Status</label>
            <select name="status" value={formData.status} onChange={handleChange} disabled={!formData.site_id} className={inputClass('status')}>
              <option value="Quoted">Quoted</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            {errors.status && <p className="text-red-500 dark:text-red-400 text-sm">{errors.status[0]}</p>}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Job')}
            </button>
          </div>
        </form>
      </div>
    </div>

    {showNewCustomerModal && (
      <CustomerModal
        onSave={async (formData) => {
          const res = await customerService.createCustomer(formData);
          const newCustomer = res.data;
          const updated = await customerService.getCustomers();
          setCustomers((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setSelectedCustomer(String(newCustomer.id));
          setFormData(prev => ({ ...prev, site_id: '' }));
          setShowNewCustomerModal(false);
        }}
        onClose={() => setShowNewCustomerModal(false)}
      />
    )}
    {showNewSiteModal && (
      <SiteModal
        site={{ customer_id: selectedCustomer }}
        onSave={async (formData) => {
          const res = await siteService.createSite(formData);
          const newSite = res.data;
          const updated = await siteService.getSites();
          setSites((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setFormData(prev => ({ ...prev, site_id: String(newSite.id) }));
          setShowNewSiteModal(false);
        }}
        onClose={() => setShowNewSiteModal(false)}
      />
    )}
  </>
  );
};

export default JobModal;
